import { Atom, AtomRegistry, Reactivity } from 'effect/reactivity';
import { AudioConversionSummary } from '@listening/api-contract/schemas';
import { Effect, Schema } from 'effect';
import { apiDataEpochAtom } from './api-runtime';
import type { AudioItem } from './audio-items';
import type { ReactivityKey } from './reactivity-keys';

/**
 * One copy of each audio the client has loaded, one atom per audio. Lists hold
 * ids and read their rows from here, so a rename, a move or a status change
 * shows in every view at once. Each audio's view is the server's copy with any
 * pending optimistic patches applied over it. A page the server renders sends
 * the rows it showed with it, under `audio:<id>`.
 */
const syncedAudioAtom = Atom.family((id: string) =>
  Atom.writable(
    (get): AudioItem | undefined => {
      get(apiDataEpochAtom);
      return undefined;
    },
    (ctx, audio: AudioItem | undefined) => ctx.setSelf(audio),
  ).pipe(
    Atom.serializable({
      key: `audio:${id}`,
      schema: Schema.UndefinedOr(AudioConversionSummary),
    }),
    Atom.keepAlive,
  ),
);

/** An optimistic change awaiting its write. Returning `undefined` hides the
 * audio, as a delete does. */
export type PendingPatch = {
  readonly id: string;
  readonly patch: (audio: AudioItem) => AudioItem | undefined;
};

const pendingPatchesAtom = Atom.make<ReadonlyArray<PendingPatch>>([]).pipe(
  Atom.keepAlive,
);

/** The audio as every view shows it, or `undefined` before it loads or after
 * it is deleted. */
export const audioRowAtom = Atom.family((id: string) =>
  Atom.make((get) =>
    get(pendingPatchesAtom)
      .filter((pending) => pending.id === id)
      .reduce<AudioItem | undefined>(
        (audio, pending) => audio && pending.patch(audio),
        get(syncedAudioAtom(id)),
      ),
  ),
);

/** Ids with a write in flight, for lists that show an optimistic arrival
 * before their refetch places it. */
export const pendingAudioIdsAtom = Atom.make(
  (get) => new Set(get(pendingPatchesAtom).map((pending) => pending.id)),
);

/** The server's copy of an audio, without pending patches. */
export const syncedAudioRowAtom: (
  id: string,
) => Atom.Atom<AudioItem | undefined> = syncedAudioAtom;

// Writes go through the registry service rather than a tracked `get`, so a
// query or mutation never subscribes to the store it fills.

/** Stores rows a read returned. */
export const writeAudios = (audios: ReadonlyArray<AudioItem>) =>
  Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    Atom.batch(() => {
      for (const audio of audios)
        registry.set(syncedAudioAtom(audio.id), audio);
    });
  });

/** The server's copy of an audio, without pending patches. */
export const syncedAudio = (id: string) => Atom.get(syncedAudioAtom(id));

/**
 * Applies `patch` to the audio while `write` runs. The patch is dropped when
 * the write settles: on success the write has stored the server's copy, on
 * failure the view falls back to it.
 */
export const withOptimisticPatch = <A, E, R>(
  id: string,
  patch: PendingPatch['patch'],
  write: Effect.Effect<A, E, R>,
) =>
  Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    const pending: PendingPatch = { id, patch };
    registry.update(pendingPatchesAtom, (patches) => [...patches, pending]);
    return yield* Effect.ensuring(
      write,
      Effect.sync(() =>
        registry.update(pendingPatchesAtom, (patches) =>
          patches.filter((entry) => entry !== pending),
        ),
      ),
    );
  });

/**
 * Reactivity keys for audio reads. Every list refetches on `lists` (an audio
 * it may never have seen) and `removed`, and on the key of each field its
 * filter or order reads; every audio's summary refetches on `summaries`. One
 * audio's reads refresh through `refreshAudios`.
 */
export const audioKeys = {
  lists: 'audio:lists',
  removed: 'audio:removed',
  field: (field: AudioListField) => `audio:field:${field}` as const,
  summaries: 'audio:summaries',
} as const satisfies Record<
  string,
  ReactivityKey | ((field: AudioListField) => ReactivityKey)
>;

/** The fields some list filters or orders by. */
export const audioListFields = [
  'conversion_status',
  'archived_at_ms',
  'folder_id',
  'created_at_ms',
  'played_at_ms',
  'title_sort_key',
] as const satisfies ReadonlyArray<keyof AudioItem>;

export type AudioListField = (typeof audioListFields)[number];

/**
 * Stores the server's copy of one audio (`null` once it is gone) and
 * refetches only the lists whose membership or order the change can affect.
 * An audio the client never had refetches every list.
 */
export const syncAudio = (id: string, next: AudioItem | null) =>
  Effect.gen(function* () {
    const registry = yield* AtomRegistry.AtomRegistry;
    const previous = registry.get(syncedAudioAtom(id));
    registry.set(syncedAudioAtom(id), next ?? undefined);
    const keys =
      next === null
        ? [audioKeys.removed]
        : previous === undefined
          ? [audioKeys.lists]
          : audioListFields
              .filter((field) => previous[field] !== next[field])
              .map(audioKeys.field);
    if (keys.length > 0) yield* Reactivity.invalidate(keys);
  });

/** Refetches every audio list, for a change the client cannot attribute to
 * one audio, such as an upload or an import. */
export const invalidateAudioLists = Reactivity.invalidate([audioKeys.lists]);
